import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import type Konva from 'konva';
import { toast } from 'sonner';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import { SetSelectionCommand } from '../../../core/commands/SetSelectionCommand';
import { getShapeManifest } from '../../../lib/diagram/registries';
import { useMediaUpload } from '../../../domains/media/hooks/useMediaUpload';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { globalEventBus } from '../../../core/events/IEventBus';
import { DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT } from '../../../domains/media/constants/mediaReleaseEvents';
import {
  getMediaUploadFriendlyMessage,
  type MediaUploadTranslateFn,
} from '../../../domains/media/utils/mediaUploadFriendlyMessage';
import { MediaUploadClientError } from '../../../domains/media/models/MediaUploadClientError';
import { Tracker } from '../../../domains/analytics/services/Tracker';
import { MEDIA } from '../../../domains/analytics/constants/MetricEvents';
import { useTranslation } from '../../../hooks/useTranslation';
import { useDiagramStore } from '../../../stores/diagramStore';
import { validateShapeLimitOrPromptUpgrade } from '../../../utils/shapeLimitUtils';
import { layoutImageDropTopLeftPositions } from '../utils/imageDropLayout';
import { MAX_RAW_IMAGE_BYTES } from '../../../domains/media/utils/imageProcessor';
import { MAX_CANVAS_DROP_IMAGE_FILES } from '../utils/canvasImageFiles';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import {
  removeStaleUploadingMediaNodes,
  useCanvasMediaInsertionInternals,
  type CanvasPoint,
} from './useCanvasMediaInsertionInternals';

const IMAGE_DROP_GRID_GAP = 16;
const MIN_FAILED_UPLOAD_PLACEHOLDER_MS = 500;

export type CanvasImageInsertSource = 'drop' | 'paste' | 'picker';
export type CanvasImagePickerUploadResult = {
  insertedCount: number;
  failures: Array<{ file: File; message: string }>;
};

interface UseCanvasImageInsertionInput {
  canWrite: boolean;
  diagramId: string;
  stageRef: RefObject<Konva.Stage | null>;
  commandManager: CommandManager;
}

type MediaIngestUrlResponse = {
  objectKey?: string;
};

export function useCanvasImageInsertion(input: UseCanvasImageInsertionInput) {
  const { canWrite, diagramId, stageRef, commandManager } = input;
  const { t } = useTranslation('diagram');
  const authenticatedFetch = useAuthenticatedFetch();
  const { uploadImage, isUploading } = useMediaUpload({ source: 'canvas.image', diagramId });
  const isInitialLoading = useDiagramStore((state) => state.isInitialLoading);
  const didRunRefreshCleanup = useRef(false);

  const defaultDimensions = useMemo(() => {
    const manifest = getShapeManifest('generic', 'image');
    return manifest?.defaultDimensions || { width: 240, height: 160 };
  }, []);

  const buildUploadedImageAttrs = useCallback((objectKey: string) => ({
    objectKey,
    fit: 'cover' as const,
    uploadState: 'ready' as const,
    uploadErrorMessage: undefined,
  }), []);

  const trackSourceEvent = useCallback((source: CanvasImageInsertSource) => {
    if (source === 'drop') {
      Tracker.track(MEDIA.CANVAS_IMAGE_DROP, { mediaKind: 'image' });
      return;
    }
    if (source === 'paste') {
      Tracker.track(MEDIA.CANVAS_IMAGE_PASTE, { mediaKind: 'image' });
      return;
    }
    Tracker.track(MEDIA.CANVAS_IMAGE_PICKER, { mediaKind: 'image' });
  }, []);

  const trackPickerBatchEvent = useCallback((count: number) => {
    Tracker.track(MEDIA.CANVAS_IMAGE_PICKER, { count });
  }, []);

  const createNodeWithObjectKey = useCallback(
    (objectKey: string, point: CanvasPoint) => {
      const cmd = new CreateShapeCommand({
        type: 'image',
        category: 'media',
        x: point.x,
        y: point.y,
        width: defaultDimensions.width,
        height: defaultDimensions.height,
        attrs: {
          objectKey,
          fit: 'cover',
        },
      });
      commandManager.execute(cmd);
      globalEventBus.emit(DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT, {
        diagramId,
        addedKeys: [objectKey],
      });
      Tracker.track(MEDIA.CANVAS_IMAGE_ADDED, { mediaKind: 'image' });
    },
    [commandManager, defaultDimensions.height, defaultDimensions.width, diagramId],
  );

  const createPendingNode = useCallback(
    (point: CanvasPoint): string | null => {
      const cmd = new CreateShapeCommand({
        type: 'image',
        category: 'media',
        x: point.x,
        y: point.y,
        width: defaultDimensions.width,
        height: defaultDimensions.height,
        attrs: {
          objectKey: '',
          fit: 'cover',
          uploadState: 'uploading',
          uploadErrorMessage: undefined,
        },
      });
      commandManager.execute(cmd);
      return cmd.getCreatedId() ?? null;
    },
    [commandManager, defaultDimensions.height, defaultDimensions.width],
  );

  const {
    markPendingNodeUploaded,
    removePendingNodeOnFailure,
    waitForMinFailedPlaceholderVisibility,
    getViewportCenter: getViewportCenterFromStage,
  } = useCanvasMediaInsertionInternals({
    commandManager,
    diagramId,
    mediaKind: 'image',
    uploadedAttrs: buildUploadedImageAttrs,
    minFailedPlaceholderMs: MIN_FAILED_UPLOAD_PLACEHOLDER_MS,
  });

  useEffect(() => {
    if (didRunRefreshCleanup.current) return;
    if (isInitialLoading) return;
    didRunRefreshCleanup.current = true;

    // Stale in-flight uploads without an object key cannot be resumed after refresh.
    const mutationManager = getNodeMutationManager();
    removeStaleUploadingMediaNodes({
      nodes: useDiagramStore.getState().nodes,
      mediaTypes: new Set(['image']),
      deleteNode: mutationManager.deleteNode.bind(mutationManager),
    });
  }, [isInitialLoading]);

  const insertImageFileAt = useCallback(
    async (file: File, point: CanvasPoint, source: CanvasImageInsertSource) => {
      if (!canWrite) return false;
      const uploadStartedAt = Date.now();
      const pendingNodeId = createPendingNode(point);
      try {
        const uploaded = await uploadImage(file);
        if (pendingNodeId) {
          markPendingNodeUploaded(pendingNodeId, uploaded.objectKey);
        } else {
          createNodeWithObjectKey(uploaded.objectKey, point);
        }
        trackSourceEvent(source);
        return true;
      } catch (error) {
        const friendly = getMediaUploadFriendlyMessage(error, t as MediaUploadTranslateFn);
        if (pendingNodeId) {
          await waitForMinFailedPlaceholderVisibility(uploadStartedAt);
          removePendingNodeOnFailure(pendingNodeId);
        }
        toast.error(friendly);
        return false;
      }
    },
    [
      canWrite,
      uploadImage,
      createPendingNode,
      markPendingNodeUploaded,
      createNodeWithObjectKey,
      trackSourceEvent,
      removePendingNodeOnFailure,
      waitForMinFailedPlaceholderVisibility,
      t,
    ],
  );

  const insertImageUrlAt = useCallback(
    async (sourceUrl: string, point: CanvasPoint, source: CanvasImageInsertSource = 'drop') => {
      if (!canWrite) return false;
      const uploadStartedAt = Date.now();
      const pendingNodeId = createPendingNode(point);
      try {
        const response = await authenticatedFetch('/api/media/ingest-url', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            sourceUrl,
            ...(diagramId.trim() ? { diagramId: diagramId.trim() } : {}),
          }),
        });
        if (!response.ok) {
          let apiError = 'Could not ingest image URL.';
          try {
            const payload = await response.json() as { error?: string };
            apiError = payload.error || apiError;
          } catch (error) {
            console.warn('[useCanvasImageInsertion] Failed to parse media ingest error response', error);
          }
          throw new MediaUploadClientError(apiError);
        }
        const payload = await response.json() as MediaIngestUrlResponse;
        if (!payload.objectKey) {
          throw new MediaUploadClientError('Upload URL response was invalid.');
        }
        if (pendingNodeId) {
          markPendingNodeUploaded(pendingNodeId, payload.objectKey);
        } else {
          createNodeWithObjectKey(payload.objectKey, point);
        }
        trackSourceEvent(source);
        return true;
      } catch (error) {
        const friendly = getMediaUploadFriendlyMessage(error, t as MediaUploadTranslateFn);
        if (pendingNodeId) {
          await waitForMinFailedPlaceholderVisibility(uploadStartedAt);
          removePendingNodeOnFailure(pendingNodeId);
        }
        toast.error(friendly);
        return false;
      }
    },
    [
      authenticatedFetch,
      canWrite,
      diagramId,
      createPendingNode,
      markPendingNodeUploaded,
      createNodeWithObjectKey,
      removePendingNodeOnFailure,
      waitForMinFailedPlaceholderVisibility,
      t,
      trackSourceEvent,
    ],
  );

  const getViewportCenterTopLeft = useCallback((): CanvasPoint | null => {
    const stage = stageRef.current;
    if (!stage) return null;
    const scaleX = stage.scaleX() || 1;
    const scaleY = stage.scaleY() || 1;
    const centerX = (stage.width() / 2 - stage.x()) / scaleX;
    const centerY = (stage.height() / 2 - stage.y()) / scaleY;
    return {
      x: centerX - defaultDimensions.width / 2,
      y: centerY - defaultDimensions.height / 2,
    };
  }, [defaultDimensions.height, defaultDimensions.width, stageRef]);

  const getViewportCenter = useCallback((): CanvasPoint | null => {
    return getViewportCenterFromStage(stageRef);
  }, [getViewportCenterFromStage, stageRef]);

  const insertImageFileAtViewportCenter = useCallback(
    async (file: File, source: CanvasImageInsertSource) => {
      const point = getViewportCenterTopLeft();
      if (!point) return false;
      return insertImageFileAt(file, point, source);
    },
    [getViewportCenterTopLeft, insertImageFileAt],
  );

  const insertDroppedImageFiles = useCallback(
    async (
      files: File[],
      anchorCenter: CanvasPoint,
      notice?: { skippedOverSize: number; skippedOverMaxCount: number },
    ): Promise<boolean> => {
      if (notice?.skippedOverMaxCount && notice.skippedOverMaxCount > 0) {
        toast.message(
          t('mediaUpload.multiDropTruncated', {
            max: String(MAX_CANVAS_DROP_IMAGE_FILES),
          }),
        );
      }
      if (notice?.skippedOverSize && notice.skippedOverSize > 0) {
        const limitMb = Math.round(MAX_RAW_IMAGE_BYTES / (1024 * 1024));
        toast.message(
          t('mediaUpload.multiDropSkippedLarge', {
            count: notice.skippedOverSize,
            limitMb: limitMb,
          }),
        );
      }
      if (!canWrite || files.length === 0) return false;

      if (files.length === 1) {
        return insertImageFileAt(
          files[0],
          {
            x: anchorCenter.x - defaultDimensions.width / 2,
            y: anchorCenter.y - defaultDimensions.height / 2,
          },
          'drop',
        );
      }

      const store = useDiagramStore.getState();
      if (!validateShapeLimitOrPromptUpgrade(store, files.length)) {
        return false;
      }

      const positions = layoutImageDropTopLeftPositions({
        count: files.length,
        anchorCenter,
        cellWidth: defaultDimensions.width,
        cellHeight: defaultDimensions.height,
        gap: IMAGE_DROP_GRID_GAP,
      });

      Tracker.track(MEDIA.CANVAS_IMAGE_DROP, { count: files.length, mediaKind: 'image' });

      const previousSelection = new Set(store.selectedIds);
      const shapeIds = files.map(
        (_, i) => `img-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 11)}`,
      );

      const creates = files.map(
        (_, i) =>
          new CreateShapeCommand({
            id: shapeIds[i],
            type: 'image',
            category: 'media',
            x: positions[i].x,
            y: positions[i].y,
            width: defaultDimensions.width,
            height: defaultDimensions.height,
            attrs: {
              objectKey: '',
              fit: 'cover',
              uploadState: 'uploading',
              uploadErrorMessage: undefined,
            },
          }),
      );

      const selectionCmd = new SetSelectionCommand(new Set(shapeIds), previousSelection);
      await commandManager.executeBatch([...creates, selectionCmd], 'Drop images');

      const results = await Promise.all(
        files.map(async (file, i) => {
          const uploadStartedAt = Date.now();
          try {
            const uploaded = await uploadImage(file);
            markPendingNodeUploaded(shapeIds[i], uploaded.objectKey);
            return { ok: true as const };
          } catch (error) {
            const friendly = getMediaUploadFriendlyMessage(error, t as MediaUploadTranslateFn);
            await waitForMinFailedPlaceholderVisibility(uploadStartedAt);
            removePendingNodeOnFailure(shapeIds[i]);
            toast.error(friendly);
            return { ok: false as const };
          }
        }),
      );

      const successes = results.filter((result) => result.ok);
      return successes.length > 0;
    },
    [
      canWrite,
      commandManager,
      defaultDimensions.height,
      defaultDimensions.width,
      insertImageFileAt,
      markPendingNodeUploaded,
      removePendingNodeOnFailure,
      waitForMinFailedPlaceholderVisibility,
      t,
      uploadImage,
    ],
  );

  return {
    defaultDimensions,
    insertImageFileAt,
    insertImageUrlAt,
    insertImageFileAtViewportCenter,
    insertDroppedImageFiles,
    insertPickerModalImageFiles: useCallback(
      async (files: File[]): Promise<CanvasImagePickerUploadResult> => {
        if (!canWrite || files.length === 0) return { insertedCount: 0, failures: [] };
        const center = getViewportCenter();
        if (!center) return { insertedCount: 0, failures: files.map((file) => ({ file, message: 'Canvas is not ready.' })) };

        const store = useDiagramStore.getState();
        if (!validateShapeLimitOrPromptUpgrade(store, files.length)) {
          return { insertedCount: 0, failures: files.map((file) => ({ file, message: t('shapeLimit.reached', { defaultValue: 'Shape limit reached.' }) })) };
        }

        const positions = layoutImageDropTopLeftPositions({
          count: files.length,
          anchorCenter: center,
          cellWidth: defaultDimensions.width,
          cellHeight: defaultDimensions.height,
          gap: IMAGE_DROP_GRID_GAP,
        });

        const results = await Promise.all(
          files.map(async (file, i) => {
            try {
              const uploaded = await uploadImage(file);
              return { ok: true as const, file, objectKey: uploaded.objectKey, point: positions[i] };
            } catch (error) {
              const friendly = getMediaUploadFriendlyMessage(error, t as MediaUploadTranslateFn);
              return { ok: false as const, file, message: friendly };
            }
          }),
        );

        const successes = results.filter((r) => r.ok);
        const failures = results.filter((r) => !r.ok).map((r) => ({ file: r.file, message: (r as { message: string }).message }));

        if (successes.length > 0) {
          const addedKeys = successes.map((s) => s.objectKey);
          const creates = successes.map((s) => new CreateShapeCommand({
            type: 'image',
            category: 'media',
            x: s.point.x,
            y: s.point.y,
            width: defaultDimensions.width,
            height: defaultDimensions.height,
            attrs: {
              objectKey: s.objectKey,
              fit: 'cover',
            },
          }));
          await commandManager.executeBatch(creates, 'Insert images');
          globalEventBus.emit(DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT, {
            diagramId,
            addedKeys,
          });
          Tracker.track(MEDIA.CANVAS_IMAGE_ADDED, { count: successes.length });
          trackPickerBatchEvent(files.length);
        }

        return { insertedCount: successes.length, failures };
      },
      [
        canWrite,
        commandManager,
        defaultDimensions.height,
        defaultDimensions.width,
        diagramId,
        getViewportCenter,
        t,
        trackPickerBatchEvent,
        uploadImage,
      ],
    ),
    isImageInserting: isUploading,
  };
}
